/**
 * Created by neo on 11.01.2026
 * Terra Provider Model - Fitness tracker provider from API
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../Services/Http/HttpBackend';

// Raw JSON from backend
export type TerraProviderJson = {
  provider: string;
  name: string;
  icon?: string;
  setup: string;
  enabled: boolean;
  types: Record<string, boolean>;
  connectedAt?: string;
  needsSystemBrowser: boolean;
  userId?: string;
  scopes?: string;
  lastWebhookUpdate?: string;
};

export type TerraAuthenticateResponse = {
  auth_url?: string;
  user_id?: string;
  status?: string;
};

// Observable model type (Date transformations + computed)
export type TerraProvider = Omit<TerraProviderJson, 'connectedAt' | 'lastWebhookUpdate'> & {
  connectedAt?: Date;
  lastWebhookUpdate?: Date;
  readonly isConnected: boolean;
  readonly supportsWeb: boolean;
  readonly logoUrl: string;
  connect(): Promise<TerraAuthenticateResponse>;
  disconnect(): Promise<any>;
  setConnected(): void;
};

// Factory function
export function createTerraProvider(json?: Partial<TerraProviderJson>): TerraProvider {
  return makeAutoObservable({
    provider: json?.provider ?? '',
    name: json?.name ?? '',
    icon: json?.icon,
    setup: json?.setup ?? '',
    enabled: json?.enabled ?? false,
    types: json?.types ?? {},
    connectedAt: json?.connectedAt ? new Date(json.connectedAt) : undefined,
    needsSystemBrowser: json?.needsSystemBrowser ?? false,
    userId: json?.userId,
    scopes: json?.scopes,
    lastWebhookUpdate: json?.lastWebhookUpdate ? new Date(json.lastWebhookUpdate) : undefined,

    get isConnected(): boolean {
      return !!this.connectedAt;
    },

    get supportsWeb(): boolean {
      return this.setup !== 'SDK';
    },

    get logoUrl(): string {
      return this.icon || `https://assets.tryterra.co/connect/${this.provider.toLowerCase()}.svg`;
    },

    connect(): Promise<TerraAuthenticateResponse> {
      const successUrl = `${window.location.origin}/tracker/terra/callback?result=success`;
      const errorUrl = `${window.location.origin}/tracker/terra/callback?result=error`;

      return HttpBackend.post(`/activity/tracker/terra/authenticate`, {
        provider: this.provider,
        successUrl,
        errorUrl,
      });
    },

    disconnect(): Promise<any> {
      return HttpBackend.delete(`/activity/tracker/terra/connection`, { provider: this.provider }).then((res) => {
        this.connectedAt = undefined;
        return res;
      });
    },

    setConnected(): void {
      this.connectedAt = new Date();
    },
  });
}

// Static API methods
export const TerraProviderApi = {
  list(): Promise<TerraProvider[]> {
    return HttpBackend.get('/activity/tracker/terra/providers').then((res: TerraProviderJson[]) =>
      res.map((r) => createTerraProvider(r)),
    );
  },
};
